import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSignOutAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Layout = ({ children, user, activeTab, setActiveTab, tabs }) => {
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (!mobile && !sidebarOpen) setSidebarOpen(true);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarOpen]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const displayName = user?.club_name || user?.sub_district_name || user?.district_name || 'Leo Club';

    return (
        <div className="flex min-h-screen bg-[#FDFDFF] font-sans selection:bg-indigo-600 selection:text-white pb-20 lg:pb-0">
            {/* -------------------- 
                DESKTOP SIDEBAR 
            -------------------- */}
            {!isMobile && (
                <aside
                    className={`
                        fixed inset-y-4 left-4 z-40 bg-white text-slate-600 transition-all duration-500 ease-in-out flex flex-col rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100
                        ${sidebarOpen ? 'w-[280px]' : 'w-24'}
                    `}
                >
                    {/* Logo Section */}
                    <div className="p-6 flex flex-col gap-6 relative">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 overflow-hidden">
                                {sidebarOpen && (
                                    <img src="/clubbw.png" alt="Leo Club Logo" className="w-21 h-21 object-contain shrink-0 mb-5 mt-5" />
                                )}
                            </div>

                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="w-8 h-8 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-all flex items-center justify-center border border-slate-100"
                            >
                                {sidebarOpen ? <FaChevronLeft size={10} /> : <FaChevronRight size={10} />}
                            </button>
                        </div>

                        {sidebarOpen && (
                            <div className="px-4 py-3 bg-slate-50 border-l-4 border-indigo-500 rounded-r-2xl transition-all duration-500">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Organization</p>
                                <h3 className="text-sm font-bold text-slate-900 leading-tight line-clamp-2">
                                    {displayName}
                                </h3>
                            </div>
                        )}
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto no-scrollbar">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-300 group relative ${activeTab === tab.id
                                    ? 'bg-indigo-50 text-indigo-600 shadow-sm'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                                    }`}
                            >
                                <span className={`text-xl flex-shrink-0 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110 text-indigo-600' : 'group-hover:scale-110'}`}>
                                    {tab.icon}
                                </span>
                                {sidebarOpen && (
                                    <span className="text-sm font-bold tracking-tight whitespace-nowrap">
                                        {tab.label}
                                    </span>
                                )}
                                {activeTab === tab.id && sidebarOpen && (
                                    <div className="absolute right-3 w-1.5 h-1.5 bg-indigo-600 rounded-full" />
                                )}
                            </button>
                        ))}
                    </nav>

                    {/* Footer User Profile & Logout */}
                    <div className="p-4 border-t border-slate-50 space-y-4 bg-[#F8FAFC]/50 rounded-b-3xl mt-auto">
                        {sidebarOpen && (
                            <div>
                            <div className="flex items-center gap-3 w-full px-3 py-2">
                               <img src="/favicon.ico" alt="Leo Club Logo" className="w-10 h-10 object-contain shrink-0 " />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[10px] font-semibold text-slate-500 truncate mb-0.5 tracking-tight">Sponsored & Designed by</span>
                                    <span className="text-xs font-black text-slate-900 truncate tracking-tight">Leo Club of SLIIT</span>
                                </div>
                            </div></div>
                        )}

                        <button
                            onClick={handleLogout}
                            title="Disconnect Session"
                            className="w-full flex items-center justify-center gap-4 px-4 py-3.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500 rounded-2xl transition-all duration-300 font-black text-xs uppercase tracking-widest"
                        >
                            <span className="text-lg flex-shrink-0"><FaSignOutAlt /></span>
                            {sidebarOpen && <span>Log Out</span>}
                        </button>
                    </div>
                </aside>
            )}

            {/* -------------------- 
                MOBILE TOP NAV 
            -------------------- */}
            {isMobile && (
                <div className="fixed top-0 inset-x-0 z-40 bg-white/90 backdrop-blur-2xl border-b border-slate-100 px-5 py-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="min-w-[36px] h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-black text-lg shadow-md shadow-indigo-500/20">
                            L
                        </div>
                        <div className="flex flex-col">
                            <h2 className="font-black text-lg tracking-tighter text-slate-900 uppercase leading-none">Leo Hub</h2>
                            <span className="text-[8px] uppercase tracking-[0.2em] font-bold text-indigo-500 truncate max-w-[120px]">
                                {user?.role?.replace('_', ' ')}
                            </span>
                        </div>
                    </div>
                    <button 
                        onClick={handleLogout}
                        className="w-9 h-9 rounded-xl border border-slate-100 bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-100 flex items-center justify-center transition-all shadow-sm"
                    >
                        <FaSignOutAlt size={14} />
                    </button>
                </div>
            )}

            {/* -------------------- 
                MOBILE BOTTOM NAV 
            -------------------- */}
            {isMobile && (
                <div className="fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-2xl border-t border-slate-100 pb-safe pt-2 px-2 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
                    <div className="flex items-center justify-around overflow-x-auto no-scrollbar py-1">
                        {tabs.map((tab) => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative flex flex-col items-center justify-center min-w-[4rem] px-2 py-2 rounded-2xl transition-all duration-300 ${isActive ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <div className={`text-xl transition-all duration-300 z-10 ${isActive ? '-translate-y-1 scale-110 drop-shadow-md' : ''}`}>
                                        {tab.icon}
                                    </div>
                                    <span className={`text-[10px] font-bold tracking-tight whitespace-nowrap transition-all duration-300 z-10 ${isActive ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
                                        {tab.label}
                                    </span>
                                    {isActive && (
                                        <div className="absolute inset-0 bg-indigo-50/80 rounded-2xl border border-indigo-100/50 -z-10 animate-fade-in" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main
                className={`flex-1 flex flex-col min-h-screen transition-all duration-500 ease-in-out
                    ${isMobile ? 'pt-20 px-4' : (sidebarOpen ? 'ml-[300px]' : 'ml-28')}
                `}
            >
                {/* Desktop Top Header (Hidden on Mobile) */}
                {!isMobile && (
                    <header className="sticky top-0 z-30 px-6 md:px-10 py-6 flex justify-between items-center h-24 bg-[#FDFDFF]/80 backdrop-blur-xl border-b border-transparent">
                        <div className="flex items-center gap-6">
                            <div className="flex flex-col border-l-4 border-indigo-600 pl-4">
                                <h4 className="text-[9px] uppercase tracking-[0.4em] font-black text-slate-400 mb-1 leading-none">{displayName}</h4>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none">
                                    {tabs.find((t) => t.id === activeTab)?.label || 'Dashboard'}
                                </h1>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 bg-white p-2 pr-4 rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-md transition-shadow cursor-default">
                            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black uppercase shadow-inner">
                                {user?.name?.charAt(0)}
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-black text-slate-900 leading-tight">{user?.name}</span>
                                <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400">
                                    {user?.role?.replace('_', ' ')}
                                </span>
                            </div>
                        </div>
                    </header>
                )}

                {/* Mobile Page Header Context */}
                {isMobile && (
                    <div className="mb-4 mt-2 px-2 border-l-4 border-indigo-600 pl-3">
                        <h4 className="text-[9px] uppercase tracking-[0.4em] font-black text-slate-400 mb-1 leading-none">{displayName}</h4>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tighter uppercase leading-none animate-fade-in">
                            {tabs.find((t) => t.id === activeTab)?.label || 'Dashboard'}
                        </h1>
                    </div>
                )}

                {/* Main Render Area */}
                <div className="flex-1 lg:p-10 w-full animate-premium pb-10">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
